// USER-FACING TASK FAMILIES, independent of the deployable's module boundaries.
//
// Riksu remains a modular monolith: recruiting, onboarding and offboarding are separate
// modules because their data, permissions and transactions differ. Exposing those seams as
// eighteen peer rows in Administration made the navigation mirror the code instead of the
// work. These folders are the presentation layer between the two: one employee-lifecycle
// family can contain three independently owned modules without coupling them.
//
// The list declares ORDER AND APPEARANCE, never membership. A destination names its folder
// once, beside its own group and requirement; `navigationEntries` derives the reachable
// members from the already-narrowed group. That preserves the central rule in
// `destinations.ts`: adding or removing a destination is not a second edit in a menu list.

import type { MessageKey } from "../shell/i18n";

interface NavigationFolderDefinition {
  readonly key: string;
  readonly label: MessageKey;
}

export const NAVIGATION_FOLDERS = [
  {
    key: "people-lifecycle",
    label: "nav.administration.peopleLifecycle",
  },
  {
    key: "time-pay-expense",
    label: "nav.administration.timePayExpense",
  },
  {
    key: "organisation-workforce",
    label: "nav.administration.organisationWorkforce",
  },
  {
    key: "automation-integrations",
    label: "nav.administration.automationIntegrations",
  },
  {
    key: "records-compliance",
    label: "nav.administration.recordsCompliance",
  },
] as const satisfies readonly NavigationFolderDefinition[];

export type NavigationFolderKey = (typeof NAVIGATION_FOLDERS)[number]["key"];

export interface FolderableNavigationEntry {
  readonly navigationFolder?: NavigationFolderKey;
}

export interface NavigationFolder<T> {
  readonly kind: "folder";
  readonly key: NavigationFolderKey;
  readonly label: MessageKey;
  readonly items: readonly T[];
}

export function isNavigationFolder<T>(
  entry: T | NavigationFolder<T>,
): entry is NavigationFolder<T> {
  return typeof entry === "object" && entry !== null && "kind" in entry && entry.kind === "folder";
}

/**
 * A group's reachable entries arranged into task families.
 *
 * Empty folders disappear. A folder with one reachable member collapses to that member, so
 * role narrowing never charges somebody an extra press merely to reveal the only place they
 * are allowed to use. Unfoldered entries come first; Administration's overview is that leaf.
 */
export function navigationEntries<T extends FolderableNavigationEntry>(
  items: readonly T[],
): readonly (T | NavigationFolder<T>)[] {
  const entries: (T | NavigationFolder<T>)[] = items.filter(
    (item) => item.navigationFolder === undefined,
  );

  for (const folder of NAVIGATION_FOLDERS) {
    const members = items.filter((item) => item.navigationFolder === folder.key);
    if (members.length === 1 && members[0]) {
      entries.push(members[0]);
    } else if (members.length > 1) {
      entries.push({ kind: "folder", ...folder, items: members });
    }
  }

  return entries;
}
